// @flow

import * as React from 'react';

import isDeepEqual from 'lodash/isEqual';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { sendInternalSearch } from '@gandi/tracking';
import { useTranslator } from '@gandi/translate';
import { Base, Button, Alert, Heading, type DialogStateProps } from '@gandi/ui';

import { ErrorBoundary } from 'src/helpers/ErrorBoundary';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { loadMoreSuggestions } from '../../modules/actions/suggestions';
import {
  selectCanLoadMore,
  selectPagination,
} from '../../modules/selectors/pagination';
import { selectPlaceholders } from '../../modules/selectors/placeholders';
import {
  selectAreAllProvided,
  selectProvided,
} from '../../modules/selectors/results';
import {
  selectSearchFirstResultLoading,
  selectSearchLoading,
  selectSearchValue,
} from '../../modules/selectors/search';

import { useSuggestedList } from '../modules/hook';

import PlaceholderList from './PlaceholderList';
import { SuggestionRow } from './SuggestionRow';

import styles from './DomainList.scss';

type Props = {
  className: string,
  provided?: boolean,
  phasesDialogState: DialogStateProps,
};

function DomainList({
  className,
  provided = false,
  phasesDialogState,
}: Props): React.MixedElement {
  const dispatch = useDispatch();

  const translator = useTranslator();

  const searchValue = useAppSelector(selectSearchValue);
  const currentPagination = useAppSelector(selectPagination);
  const { suggestions } = useSuggestedList();

  const placeholders = useAppSelector(state =>
    selectPlaceholders(state, provided),
  );
  const searchLoading = useAppSelector(selectSearchLoading);
  const allProvided = useAppSelector(selectAreAllProvided);
  const canLoadMore = useAppSelector(selectCanLoadMore);
  const firstResultLoading = useAppSelector(selectSearchFirstResultLoading);

  const currentProvided = useAppSelector(selectProvided);

  const showList = suggestions.length > 0;
  const showPlaceholders = placeholders > 0;
  const hasPagination = !!searchValue && canLoadMore;

  const [searchParams] = useSearchParams();
  const hasBulk = searchParams.has('bulk');

  const isFirstLoading = !showList && showPlaceholders;

  const showHeadingPlaceholder =
    isFirstLoading && !hasBulk && searchValue.includes('.');

  React.useEffect(() => {
    sendInternalSearch({
      type: 'display',
      keyword: searchValue,
      page: currentPagination?.page,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePagination = React.useRef(currentPagination);
  const updateSearchValue = React.useRef(searchValue);
  React.useEffect(() => {
    const paginationChanged =
      !updatePagination.current ||
      !isDeepEqual(currentPagination, updatePagination.current);

    if (paginationChanged || updateSearchValue.current !== searchValue) {
      sendInternalSearch({
        type: 'display',
        keyword: searchValue,
        page: currentPagination?.page,
      });
    }
  }, [currentPagination, searchValue]);

  const showLoadMoreButton =
    hasPagination && showList && (!provided || allProvided);

  const loadMoreLabel =
    provided && allProvided
      ? translator.__('Load more responses')
      : translator.__('Load more suggestions');

  const showAlert =
    !!searchValue &&
    !global.__SERVER__ &&
    !searchLoading &&
    !provided &&
    !allProvided &&
    suggestions.length === 0;

  const loadMore: () => void = () => {
    dispatch(loadMoreSuggestions());
  };

  return (
    <div className={className}>
      {(showList || showHeadingPlaceholder) && (
        <>
          {(currentProvided.length > 0 || showHeadingPlaceholder) && (
            <Heading
              as="h2"
              fontSize="l"
              marginBottom="small"
              visibility={showHeadingPlaceholder ? 'hidden' : 'visible'}
            >
              {translator.__('You should like as well')}
            </Heading>
          )}
          <Base
            tagName="ul"
            listStyleType="none"
            marginX="none"
            marginTop="none"
            marginBottom="base"
            padding="none"
          >
            {suggestions.map(fqdn => (
              <li key={fqdn} className={styles.suggestionRows}>
                <ErrorBoundary fallback={<SuggestionRow.Error fqdn={fqdn} />}>
                  <SuggestionRow
                    fqdn={fqdn}
                    phasesDialogState={phasesDialogState}
                  />
                </ErrorBoundary>
              </li>
            ))}
          </Base>
        </>
      )}
      {showPlaceholders && !hasBulk && <PlaceholderList count={placeholders} />}
      {showAlert && (
        <Base marginBottom="base">
          <Alert
            message={translator.__('No results found for your search.')}
            intent="info"
          />
        </Base>
      )}

      {showLoadMoreButton && (
        <Base flex flexJustifyCenter>
          <Button
            type="button"
            className={styles.button}
            onClick={loadMore}
            kind="outline"
            loading={firstResultLoading}
          >
            {loadMoreLabel}
          </Button>
        </Base>
      )}
    </div>
  );
}

export { DomainList };
